import { CompanyCatalog } from "domain/company/companyConfig";

import { Material, Pannel, Province } from "types/canvasTypes";

import { ATTACHMENT_VARIANTS } from "./attachments";
import {
  glassList36H,
  glassList42H,
  glassList48H,
  glassList48R,
  glassListOtherR,
} from "./glassPanels";

/**
 * MAP (Minimum Advertised Price) catalog for the reseller domain.
 * Prices sourced from Firebase rf-transparent project, public/*-MAP documents.
 * Uses a single price for both provinces since MAP pricing is province-agnostic.
 */

// --- Glass Panel MAP Prices (width → price) ---

const panels36MapPrices: Record<number, number> = {
  7.9: 88, 8.9: 89.6, 9.8: 91.2, 10.9: 96, 11.8: 100.8,
  13.8: 105.6, 15.7: 110.4, 17.7: 115.2, 19.7: 123.2, 21.7: 128,
  23.6: 136, 25.6: 144, 27.6: 152, 29.5: 160, 31.5: 168,
  33.5: 176, 35.4: 184, 37.4: 192, 39.4: 200, 41.3: 203.2,
  43.3: 208, 45.3: 214.4, 47.2: 220.8, 49.2: 227.2, 51.2: 232,
  53.1: 235.2, 55.1: 240, 57.7: 244.8, 59.1: 251.2, 61: 256,
  63: 320, 65: 336, 66.9: 352, 68.9: 376, 70.9: 400, 72.8: 416,
};

const panels42MapPrices: Record<number, number> = {
  7.9: 104, 8.9: 105.6, 9.8: 107.2, 10.9: 112, 11.8: 120,
  13.8: 128, 15.7: 136, 17.7: 144, 19.7: 148.8, 21.7: 153.6,
  23.6: 160, 25.6: 168, 27.6: 176, 29.5: 179.2, 31.5: 184,
  33.5: 192, 35.4: 208, 37.4: 216, 39.4: 224, 41.3: 232,
  43.3: 236.8, 45.3: 243.2, 47.2: 248, 49.2: 251.2, 51.2: 256,
  53.1: 260.8, 55.1: 264, 57.7: 267.2, 59.1: 272, 61: 288,
  63: 320, 65: 344, 66.9: 360, 68.9: 392, 70.9: 440, 72.8: 456,
};

const panels48MapPrices: Record<number, number> = {
  7.9: 120, 8.9: 121.6, 9.8: 123.2, 10.9: 128, 11.8: 132.8,
  13.8: 136, 15.7: 139.2, 17.7: 147.2, 19.7: 152, 21.7: 160,
  23.6: 168, 25.6: 176, 27.6: 179.2, 29.5: 184, 31.5: 192,
  33.5: 200, 35.4: 216, 37.4: 232, 39.4: 240, 41.3: 248,
  43.3: 256, 45.3: 264, 47.2: 272, 49.2: 280, 51.2: 288,
  53.1: 296, 55.1: 304, 57.7: 312, 59.1: 320, 61: 328,
  63: 344, 65: 352, 66.9: 368, 68.9: 416, 70.9: 448, 72.8: 478.4,
};

const rPanels48MapPrices: Record<number, number> = {
  11.8: 184, 13.8: 192, 15.7: 208, 17.7: 216, 19.7: 224,
  21.7: 232, 23.6: 240, 25.6: 248, 27.6: 251.2, 29.5: 254.4,
  31.5: 257.6, 33.5: 259.2, 35.4: 264, 37.4: 273.6, 39.4: 278.4,
  41.3: 288, 43.3: 294.4, 45.3: 302.4, 47.2: 307.2, 49.2: 313.6,
  51.2: 320, 53.1: 323.2, 55.1: 329.6, 57.7: 337.6, 59.1: 345.6,
  61: 348.8, 63: 355.2, 65: 363.2, 66.9: 369.6, 68.9: 377.6,
};

const rPanelsOtherMapPrice = 288; // panels36-MAP & panels42-MAP rPanelList

function mapPrice(price: number): Map<Province, number> {
  return new Map([
    [Province.Quebec, price],
    [Province.Ontario, price],
  ]);
}

function applyMapPrices(
  panels: Pannel[],
  priceMap: Record<number, number>
): Pannel[] {
  return panels
    .filter((p) => priceMap[p.length] !== undefined)
    .map((p) => ({
      ...p,
      price: mapPrice(priceMap[p.length]),
    }));
}

// --- Attachment MAP Prices ---

const RESELLER_ATTACHMENT_VARIANTS = {
  [Material.AdjustableAttachement]: {
    ...ATTACHMENT_VARIANTS[Material.AdjustableAttachement],
    costBlack: mapPrice(64),
    costSilver: mapPrice(60),
  },
  [Material.Pannel90Attachemnt]: {
    ...ATTACHMENT_VARIANTS[Material.Pannel90Attachemnt],
    costBlack: mapPrice(44),
    costSilver: mapPrice(40),
  },
  [Material.WallAttachment]: {
    ...ATTACHMENT_VARIANTS[Material.WallAttachment],
    costBlack: mapPrice(44),
    costSilver: mapPrice(40),
  },
  [Material.Spigots]: {
    ...ATTACHMENT_VARIANTS[Material.Spigots],
    costBlack: mapPrice(116),
    costSilver: mapPrice(106),
  },
  [Material.Pannel180Attachemnt]: {
    ...ATTACHMENT_VARIANTS[Material.Pannel180Attachemnt],
    costBlack: mapPrice(44),
    costSilver: mapPrice(40),
  },
  [Material.DoorLatch]: {
    ...ATTACHMENT_VARIANTS[Material.DoorLatch],
    costBlack: mapPrice(200),
    costSilver: mapPrice(150),
  },
  [Material.FloorLock]: {
    ...ATTACHMENT_VARIANTS[Material.FloorLock],
    costBlack: mapPrice(200),
    costSilver: mapPrice(160),
  },
  [Material.HingeKit]: {
    ...ATTACHMENT_VARIANTS[Material.HingeKit],
    costBlack: mapPrice(350),
    costSilver: mapPrice(300),
  },
};

// --- Exported Catalog ---

export const resellerCatalog: CompanyCatalog = {
  doorWidth: 36,
  doorCost: mapPrice(288),
  glassPanels: {
    h36: applyMapPrices(glassList36H, panels36MapPrices),
    h42: applyMapPrices(glassList42H, panels42MapPrices),
    h48: applyMapPrices(glassList48H, panels48MapPrices),
    r48: applyMapPrices(glassList48R, rPanels48MapPrices),
    rOther: glassListOtherR.map((p) => ({
      ...p,
      price: mapPrice(rPanelsOtherMapPrice),
    })),
  },
  attachments: RESELLER_ATTACHMENT_VARIANTS as unknown as Record<Material, import("types/canvasTypes").Attachment>,
};
